import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowDownRight, CheckCircle2, MessageSquare, AlertTriangle, Clock, Zap, Download } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from '../components/ui/dialog';
import { isAfterHoursCT, timeUntilOpenCT } from '../lib/payoutHours';
import BrandedReceipt from './BrandedReceipt';
import LoadBonusPromo from './LoadBonusPromo';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const FALLBACK_PHONE = '256-746-3933';
const LARGE_PAYOUT_THRESHOLD = 1000;
const LARGE_PAYOUT_HOURS = 24;  // was 48 — updated Feb 2026 for new fee model
const FAST_PAYOUT_HOURS = 24;    // (Jun 2026) fast-tier no longer promises 3h; fallback matches the 24h window
const CASHOUT_FEE_PCT = 0.06;   // 6% fee → rebated as Free Play every Monday
const NORMAL_PAYOUT_HOURS = 24;

const CashOutDialog = ({ triggerClassName = '', compact = false, open: openProp, onOpenChange: onOpenChangeProp, hideTrigger = false }) => {
    const { t } = useTranslation();
    const { user, updateCredits } = useAuth();
    const [dialogOpen, setDialogOpen] = useState(false);
    const [cashOutAmount, setCashOutAmount] = useState('');
    const [payoutMethod, setPayoutMethod] = useState('');
    const [payoutHandle, setPayoutHandle] = useState('');
    // TapSNDR ticket fields — collected once and remembered for next time.
    const [payoutEmail, setPayoutEmail] = useState('');
    const [payoutNameOnAccount, setPayoutNameOnAccount] = useState('');
    // Recent handles per method — populated from the /user/cashout-handle-book
    // endpoint. Rendered as one-tap prefill chips under the handle input so
    // repeat cashouts to the same account never require re-typing.
    const [handleBook, setHandleBook] = useState({});
    // Optional QR code screenshot — Cash App / Venmo. Stored as a base64
    // data URL right on the transaction so the cashier can preview + scan
    // when redeeming through TapSNDR.
    const [payoutQrDataUrl, setPayoutQrDataUrl] = useState('');
    // Player-saved QRs, one per method — auto-populates payoutQrDataUrl
    // when the player picks a method they've previously saved a QR for.
    const [savedQrs, setSavedQrs] = useState({});
    const [saveQrToProfile, setSaveQrToProfile] = useState(true);
    // Quick-Cashout mode — when the player has all their info from a prior
    // cashout, we hide the full form by default and offer amount chips + a
    // big Cash Out button. Tapping "Change details" reveals the full form.
    const [showFullForm, setShowFullForm] = useState(false);
    // Snapshot of Quick-Cashout eligibility at the moment the dialog opened.
    // This is INTENTIONALLY not derived from live form state — otherwise
    // typing a handle into the full form would flip the UI into Quick mode
    // mid-keystroke, hiding the input the player is trying to fill.
    // Only set to `true` when the server-side prefill was already complete
    // enough to safely one-tap submit.
    const [quickModeEligible, setQuickModeEligible] = useState(false);
    const [cashOutLoading, setCashOutLoading] = useState(false);
    const [cashOutError, setCashOutError] = useState('');
    const [cashOutSuccess, setCashOutSuccess] = useState(false);
    const [enabledMethods, setEnabledMethods] = useState([]);
    const [supportPhone, setSupportPhone] = useState(FALLBACK_PHONE);
    const [lastMethod, setLastMethod] = useState('');
    const [lastHandle, setLastHandle] = useState('');
    const [acknowledgedLarge, setAcknowledgedLarge] = useState(false);
    const [submittedAsLarge, setSubmittedAsLarge] = useState(false);
    const [submittedMethod, setSubmittedMethod] = useState('');
    const [submittedAmount, setSubmittedAmount] = useState(0);
    // Fee/net breakdown persisted from the /user/cashout response so the
    // success screen can restate exactly what will hit the player's handle
    // and when. Requested 2026-08-01: give a crisp post-submit receipt.
    const [submittedFee, setSubmittedFee] = useState(0);
    const [submittedNet, setSubmittedNet] = useState(0);
    const [submittedHandle, setSubmittedHandle] = useState('');
    const [submittedEtaHours, setSubmittedEtaHours] = useState(FAST_PAYOUT_HOURS);
    // Was the request submitted while the payout desk was closed?
    // Captured at submit-time so the receipt copy reflects reality even if
    // the player opens the app past 10 PM and clicks around for a bit.
    const [submittedAfterHours, setSubmittedAfterHours] = useState(false);
    const [submittedRef, setSubmittedRef] = useState('');
    const [showBrandedReceipt, setShowBrandedReceipt] = useState(false);
    // Live countdown that ticks every 30 seconds while the dialog is open
    // after-hours. Refresh interval is coarse (30s is fine for an hh:mm
    // display) and stops when the dialog closes to avoid useless timers.
    const [openCountdown, setOpenCountdown] = useState(() => timeUntilOpenCT());
    useEffect(() => {
        if (!isAfterHoursCT()) return undefined;
        const id = setInterval(() => setOpenCountdown(timeUntilOpenCT()), 30_000);
        return () => clearInterval(id);
    }, [dialogOpen, openProp]);

    const parsedAmount = parseFloat(cashOutAmount);
    const isLargePayout = !isNaN(parsedAmount) && parsedAmount >= LARGE_PAYOUT_THRESHOLD;
    // Fee breakdown (Feb 2026 — 6% fee, rebated as Free Play every Monday)
    const feeAmount = !isNaN(parsedAmount) && parsedAmount > 0
        ? Math.round(parsedAmount * CASHOUT_FEE_PCT * 100) / 100
        : 0;
    const netAmount = !isNaN(parsedAmount) && parsedAmount > 0
        ? Math.round((parsedAmount - feeAmount) * 100) / 100
        : 0;
    const speedHours = isLargePayout ? LARGE_PAYOUT_HOURS : FAST_PAYOUT_HOURS;

    const fetchCashoutSettings = async () => {
        try {
            const { data } = await axios.get(`${API}/cashout-settings`, { withCredentials: true });
            setEnabledMethods(data.methods || []);
            setSupportPhone(data.support_phone || FALLBACK_PHONE);
            setLastMethod(data.last_payout_method || '');
            setLastHandle(data.last_payout_handle || '');
            // Prefill TapSNDR ticket fields from the player's last cashout
            // (or their signup email as a fallback for the very first request).
            setPayoutEmail(data.last_payout_email || '');
            setPayoutNameOnAccount(data.last_payout_name_on_account || '');
        } catch {
            setEnabledMethods([]);
        }
    };

    useEffect(() => { fetchCashoutSettings(); }, []);

    // Fetch the recent-handles book once when the dialog is open so we can
    // render prefill chips. Silent-fails if the player has no history.
    useEffect(() => {
        const isOpen = typeof openProp === 'boolean' ? openProp : dialogOpen;
        if (!isOpen) return;
        let cancelled = false;
        (async () => {
            try {
                const [{ data: bk }, { data: qrs }] = await Promise.all([
                    axios.get(`${API}/user/cashout-handle-book`, { withCredentials: true }),
                    axios.get(`${API}/user/saved-payout-qrs`, { withCredentials: true }),
                ]);
                if (cancelled) return;
                setHandleBook(bk?.handles_by_method || {});
                setSavedQrs(qrs?.saved_payout_qrs || {});
            } catch { /* silent */ }
        })();
        return () => { cancelled = true; };
    }, [openProp, dialogOpen]);

    // Auto-load the saved QR (if any) whenever the player picks a method.
    // Player can still swap or remove it before submitting.
    useEffect(() => {
        if (!payoutMethod) return;
        const saved = savedQrs[payoutMethod];
        if (saved) setPayoutQrDataUrl(saved);
        else setPayoutQrDataUrl('');
    }, [payoutMethod, savedQrs]);

    useEffect(() => {
        let cancelled = false;
        const apply = async () => {
            if (cancelled) return;
            if (dialogOpen && lastMethod) {
                const stillEnabled = enabledMethods.find(m => m.id === lastMethod);
                if (stillEnabled) {
                    setPayoutMethod(lastMethod);
                    if (stillEnabled.needs_handle) setPayoutHandle(lastHandle || '');
                }
            }
        };
        apply();
        return () => { cancelled = true; };
    }, [dialogOpen, lastMethod, lastHandle, enabledMethods]);

    // Snapshot Quick-Cashout eligibility ONCE per dialog open, from the
    // values the server prefilled. If the server didn't give us enough to
    // one-tap safely, force the full form for this session so the player
    // can type without the UI switching out from under them mid-keystroke.
    useEffect(() => {
        const isOpen = typeof openProp === 'boolean' ? openProp : dialogOpen;
        if (!isOpen) {
            // Reset when the dialog closes so a subsequent open re-snapshots.
            setQuickModeEligible(false);
            return;
        }
        if (!enabledMethods.length || !lastMethod) return;
        const method = enabledMethods.find(m => m.id === lastMethod);
        if (!method) return;
        const handleOk = !method.needs_handle
            || (lastHandle && lastHandle.trim().length >= MIN_HANDLE_LEN);
        const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((payoutEmail || '').trim());
        const nameOk = (payoutNameOnAccount || '').trim().split(/\s+/).length >= 2;
        const creditsOk = availableCredits >= MIN_CASHOUT;
        if (handleOk && emailOk && nameOk && creditsOk) {
            setQuickModeEligible(true);
        }
        // We deliberately DON'T flip back to false if the user edits later —
        // the snapshot is one-way. Once the player is in Quick mode, they
        // stay until they hit "Change details" or close the dialog.
    }, [dialogOpen, openProp, enabledMethods, lastMethod, lastHandle]);

    const selectedMethod = enabledMethods.find(m => m.id === payoutMethod);
    const needsHandle = selectedMethod?.needs_handle;
    const MIN_CASHOUT = 5;
    // Minimum handle length before we'll accept it for one-tap Quick mode.
    // A $ or @ plus a real username is at least 3 chars, and Zelle uses a
    // 10-digit phone or an email. This guards against garbage single-char
    // handles left behind by aborted cashouts (e.g. "N").
    const MIN_HANDLE_LEN = 3;

    // Quick-Cashout eligibility — everything TapSNDR needs is already on
    // file from a prior cashout, so a returning player can pick an amount
    // and submit with a single tap. Cash-in-person payouts still work in
    // Quick mode (they don't need a handle at all).
    const availableCredits = Math.floor((user?.credits || 0) * 100) / 100;
    // Live eligibility check — used only for computing the snapshot at
    // dialog-open time. Requires the saved handle to be at least
    // MIN_HANDLE_LEN so a stale single-char handle can't trigger Quick
    // mode.
    const canQuickCashout = Boolean(
        payoutMethod &&
        (!needsHandle || (payoutHandle && payoutHandle.trim().length >= MIN_HANDLE_LEN)) &&
        payoutEmail && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(payoutEmail.trim()) &&
        payoutNameOnAccount && payoutNameOnAccount.trim().split(/\s+/).length >= 2 &&
        availableCredits >= MIN_CASHOUT
    );
    // Quick-amount chips — a few sensible round numbers below the player's
    // balance, plus a "Cash out all" chip. Deduped and clamped to the
    // player's actual credits so we never suggest an invalid amount.
    const quickAmounts = (() => {
        const chips = [];
        const roundBuckets = [50, 100, 200, 500, 1000];
        for (const v of roundBuckets) {
            if (v <= availableCredits) chips.push(v);
        }
        // Cap at 4 rounded chips + the "All" chip so we don't overflow.
        return chips.slice(-4);
    })();

    const handleCashOut = async () => {
        const amount = parseFloat(cashOutAmount);
        if (isNaN(amount) || amount <= 0) {
            setCashOutError('Please enter a valid amount');
            return;
        }
        if (amount < MIN_CASHOUT) {
            setCashOutError(`Minimum cashout is $${MIN_CASHOUT}.00`);
            return;
        }
        if (amount > (user?.credits || 0)) {
            setCashOutError('Insufficient credits');
            return;
        }
        if (!payoutMethod) {
            setCashOutError('Please choose how you want to be paid');
            return;
        }
        if (needsHandle && !payoutHandle.trim()) {
            setCashOutError(`Please enter your ${selectedMethod.label} handle so we know where to send your money`);
            return;
        }
        // TapSNDR ticket fields — required so the cashier can complete the
        // redemption without pinging the player back.
        const emailTrim = payoutEmail.trim();
        if (!emailTrim || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailTrim)) {
            setCashOutError('Please enter a valid email so we can send your payout ticket');
            return;
        }
        const nameTrim = payoutNameOnAccount.trim();
        if (!nameTrim || nameTrim.split(/\s+/).length < 2) {
            setCashOutError('Please enter the first AND last name on your payment account');
            return;
        }
        // Large payouts require explicit acknowledgement of the 48-hour SLA
        if (amount > LARGE_PAYOUT_THRESHOLD && !acknowledgedLarge) {
            setCashOutError(`Please acknowledge the up-to-${LARGE_PAYOUT_HOURS}-hour processing window for cashouts over $${LARGE_PAYOUT_THRESHOLD.toLocaleString()}`);
            return;
        }

        setCashOutLoading(true);
        setCashOutError('');

        try {
            const { data } = await axios.post(
                `${API}/user/cashout`,
                {
                    amount,
                    payout_method: payoutMethod,
                    payout_handle: needsHandle ? payoutHandle.trim() : '',
                    payout_email: payoutEmail.trim(),
                    payout_name_on_account: payoutNameOnAccount.trim(),
                    payout_qr_data_url: payoutQrDataUrl || '',
                },
                { withCredentials: true }
            );
            updateCredits(data.new_balance);
            setSubmittedAsLarge(!!data.large_payout);
            setSubmittedMethod(payoutMethod);
            setSubmittedAmount(parseFloat(cashOutAmount) || 0);
            setSubmittedFee(Number(data.fee_amount) || 0);
            setSubmittedNet(Number(data.net_amount) || (parseFloat(cashOutAmount) || 0));
            setSubmittedHandle(needsHandle ? payoutHandle.trim() : '');
            setSubmittedEtaHours(Number(data.expected_max_hours) || (data.large_payout ? LARGE_PAYOUT_HOURS : FAST_PAYOUT_HOURS));
            setSubmittedAfterHours(isAfterHoursCT());
            setSubmittedRef(String(data.transaction_id || data.id || Date.now()).slice(-8));
            // Persist the QR to profile if the player opted in and there's
            // a new/changed image. Fire-and-forget — never blocks the
            // success panel from rendering.
            if (payoutQrDataUrl && saveQrToProfile && ['Cash App', 'Venmo', 'Chime'].includes(payoutMethod)) {
                axios.post(`${API}/user/save-payout-qr`, {
                    method: payoutMethod, data_url: payoutQrDataUrl,
                }, { withCredentials: true }).catch(() => { /* silent */ });
            }
            setCashOutSuccess(true);
            setCashOutAmount('');
            fetchCashoutSettings();
        } catch (error) {
            setCashOutError(error.response?.data?.detail || 'Cash out failed');
        } finally {
            setCashOutLoading(false);
        }
    };

    const handleDialogChange = (open) => {
        setDialogOpen(open);
        if (typeof onOpenChangeProp === 'function') onOpenChangeProp(open);
        if (!open) {
            setCashOutSuccess(false);
            setCashOutError('');
            setCashOutAmount('');
            setPayoutMethod('');
            setPayoutHandle('');
            setPayoutQrDataUrl('');
            setAcknowledgedLarge(false);
            setSubmittedAsLarge(false);
            setSubmittedMethod('');
            setSubmittedAmount(0);
            setSubmittedFee(0);
            setSubmittedNet(0);
            setSubmittedHandle('');
            setSubmittedEtaHours(FAST_PAYOUT_HOURS);
            setSubmittedAfterHours(false);
            setShowFullForm(false);
        }
    };

    if (!user || user.role === 'admin') return null;

    const isControlled = typeof openProp === 'boolean';
    const effectiveOpen = isControlled ? openProp : dialogOpen;

    return (
        <Dialog open={effectiveOpen} onOpenChange={handleDialogChange}>
            {!hideTrigger && (
            <DialogTrigger asChild>
                <Button
                    className={`btn-primary rounded-full relative ${compact ? 'px-3 py-1.5 text-xs h-8' : 'px-4 py-2 text-sm'} ${triggerClassName}`}
                    disabled={(user?.credits || 0) <= 0}
                    data-testid="cashout-trigger"
                >
                    <ArrowDownRight className={`${compact ? 'w-3.5 h-3.5' : 'w-4 h-4'} mr-1`} />
                    {t('common.cash_out')}
                </Button>
            </DialogTrigger>
            )}
            <DialogContent className="bg-[#121212] border-white/10 max-w-md max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle className="text-white">
                        {cashOutSuccess ? 'Request Received!' : 'Request Cash Out'}
                    </DialogTitle>
                    {!cashOutSuccess && (
                        <DialogDescription className="text-[#A0AAB2]">
                            Available: {(user?.credits ?? 0).toFixed(2)} credits
                            {Number(user?.bonus_credits || 0) > 0 && (
                                <span className="block text-[11px] text-[#39FF14]" data-testid="cashout-free-play-note">
                                    ${Number(user.bonus_credits).toFixed(2)} Free Play can be played on any game; it becomes cash once it is played through.
                                </span>
                            )}
                        </DialogDescription>
                    )}
                </DialogHeader>

                {!cashOutSuccess && <LoadBonusPromo compact context="playback" />}

                {cashOutSuccess ? (
                    <div className="py-4 text-center" data-testid="cashout-success-panel">
                        <div className="w-16 h-16 mx-auto rounded-full bg-[#39FF14]/20 flex items-center justify-center mb-4">
                            <CheckCircle2 className="w-8 h-8 text-[#39FF14]" />
                        </div>
                        <p className="text-[#39FF14] font-bold text-lg mb-3">
                            We&apos;ve received your cash out!
                        </p>

                        {/* ── Payment Receipt (Aug 2026) ─────────────────────
                            Give the player a crystal-clear "you will receive
                            $X within Y hours via {method} to {handle}" line so
                            they never wonder where their money is going or how
                            much the 6% fee shaved off. */}
                        <div
                            className="rounded-xl p-4 mb-4 border-2 text-left"
                            style={{
                                background: 'linear-gradient(135deg, rgba(57,255,20,0.10), rgba(0,229,255,0.06))',
                                borderColor: 'rgba(57,255,20,0.45)',
                            }}
                            data-testid="cashout-receipt-card"
                        >
                            <p className="text-[10px] uppercase tracking-widest text-[#A0AAB2] font-bold mb-1">
                                You will receive
                            </p>
                            <div className="flex items-baseline gap-2 mb-2">
                                <span
                                    className="text-3xl font-black text-[#39FF14] font-mono tabular-nums"
                                    data-testid="cashout-receipt-net"
                                >
                                    ${submittedNet.toFixed(2)}
                                </span>
                                <span className="text-xs text-[#A0AAB2]">
                                    {submittedAfterHours
                                        ? 'after 8 AM CT'
                                        : (submittedAsLarge
                                            ? `within ${submittedEtaHours} hours`
                                            : 'during payout desk hours')}
                                </span>
                            </div>

                            <div className="border-t border-white/10 pt-2 space-y-1 text-[12px]">
                                <div className="flex justify-between text-white/85">
                                    <span>Requested</span>
                                    <span
                                        className="font-mono tabular-nums"
                                        data-testid="cashout-receipt-gross"
                                    >
                                        ${submittedAmount.toFixed(2)}
                                    </span>
                                </div>
                                {submittedFee > 0 && (
                                    <div className="flex justify-between text-[#FFB84D]">
                                        <span>6% fee (rebated Monday as Free Play)</span>
                                        <span
                                            className="font-mono tabular-nums"
                                            data-testid="cashout-receipt-fee"
                                        >
                                            −${submittedFee.toFixed(2)}
                                        </span>
                                    </div>
                                )}
                                <div className="flex justify-between text-white font-bold pt-1 border-t border-white/10">
                                    <span>Net payout</span>
                                    <span className="font-mono tabular-nums text-[#39FF14]">
                                        ${submittedNet.toFixed(2)}
                                    </span>
                                </div>
                            </div>

                            <div className="border-t border-white/10 mt-3 pt-2 space-y-1 text-[12px]">
                                <div className="flex justify-between items-center gap-2">
                                    <span className="text-[#A0AAB2]">Payment method</span>
                                    <span
                                        className="text-white font-semibold text-right truncate max-w-[60%]"
                                        data-testid="cashout-receipt-method"
                                    >
                                        {submittedMethod || '—'}
                                    </span>
                                </div>
                                {submittedHandle && (
                                    <div className="flex justify-between items-center gap-2">
                                        <span className="text-[#A0AAB2]">Sent to</span>
                                        <span
                                            className="text-[#FFD700] font-mono font-semibold text-right truncate max-w-[60%]"
                                            data-testid="cashout-receipt-handle"
                                            title={submittedHandle}
                                        >
                                            {submittedHandle}
                                        </span>
                                    </div>
                                )}
                            </div>

                            {submittedFee > 0 && (
                                <p className="mt-3 text-[10.5px] leading-snug text-[#A0AAB2]">
                                    Good news — that ${submittedFee.toFixed(2)} fee comes back to you as Free Play
                                    this coming Monday. Play it through 1× and any winnings are cashable.
                                </p>
                            )}
                        </div>

                        {/* ============================================
                            LOAD-WARNING on the SUCCESS receipt too — this
                            is the moment they've most recently thought
                            about the cashier's handle, so hammer the
                            "loads only via PayPal" rule right here.
                           ============================================ */}
                        <div
                            className="rounded-xl p-3 border-2 mb-4 text-left"
                            style={{
                                background: 'linear-gradient(135deg, rgba(255,59,48,0.14), rgba(255,0,127,0.05))',
                                borderColor: 'rgba(255,59,48,0.65)',
                            }}
                            data-testid="cashout-success-load-warning"
                        >
                            <div className="flex items-start gap-2">
                                <AlertTriangle className="w-5 h-5 text-[#FF3B30] shrink-0 mt-0.5" />
                                <div className="text-white text-[12.5px] leading-snug">
                                    <p className="font-black text-[#FF3B30] uppercase text-sm">
                                        Next time you load, use PayPal only
                                    </p>
                                    <p className="mt-1">
                                        Do <span className="text-[#FF3B30] font-bold">NOT</span> send loads back to
                                        the handle that paid you — those funds are <span className="underline">lost</span>.
                                        Only the PayPal link on your dashboard adds credits.
                                    </p>
                                </div>
                            </div>
                        </div>

                        {submittedAsLarge ? (
                            <div className="bg-[#FFD700]/10 border border-[#FFD700]/40 rounded-xl p-4 mb-4 text-left">
                                <div className="flex items-center gap-2 mb-1.5">
                                    <Clock className="w-4 h-4 text-[#FFD700]" />
                                    <p className="text-[#FFD700] font-bold text-sm">Allow up to {submittedEtaHours} hours</p>
                                </div>
                                <p className="text-white text-xs leading-relaxed">
                                    For cashouts over ${LARGE_PAYOUT_THRESHOLD.toLocaleString()}, our admin team may need
                                    up to <span className="text-[#FFD700] font-bold">{submittedEtaHours} hours</span> to
                                    process payment. Most go out much faster — usually same day. You&apos;ll be paid via the
                                    method you chose.
                                </p>
                            </div>
                        ) : submittedAfterHours ? (
                            <div
                                className="bg-[#FFB84D]/10 border border-[#FFB84D]/50 rounded-xl p-4 mb-4 text-left"
                                data-testid="cashout-after-hours-notice"
                            >
                                <div className="flex items-center gap-2 mb-1.5">
                                    <Clock className="w-4 h-4 text-[#FFB84D]" />
                                    <p className="text-[#FFB84D] font-bold text-sm">
                                        Queued for morning — payout desk is closed
                                    </p>
                                </div>
                                <p className="text-white text-xs leading-relaxed">
                                    Cashouts submitted between <span className="font-bold">10 PM and 8 AM CT</span> are
                                    processed the moment customer service reopens at
                                    {' '}<span className="text-[#FFB84D] font-bold">8 AM CT</span>. Your request is safely
                                    in the queue — you&apos;ll get a notification the second it&apos;s approved.
                                </p>
                            </div>
                        ) : (
                            <div className="bg-[#39FF14]/10 border border-[#39FF14]/40 rounded-xl p-4 mb-4 text-left" data-testid="cashout-sla-notice">
                                <div className="flex items-center gap-2 mb-1.5">
                                    <Clock className="w-4 h-4 text-[#39FF14]" />
                                    <p className="text-[#39FF14] font-bold text-sm">In the payout queue</p>
                                </div>
                                <p className="text-white text-xs leading-relaxed">
                                    Your request is in the queue and will be processed as quickly as possible during
                                    {' '}<span className="text-[#39FF14] font-bold">payout desk hours (8 AM–10 PM CT)</span>.
                                    You&apos;ll get a notification the moment it&apos;s approved and again when it&apos;s paid.
                                </p>
                            </div>
                        )}

                        <div className="bg-[#0a0a0a] rounded-xl p-4 border border-[#FFD700]/20">
                            <p className="text-[#A0AAB2] text-xs mb-1">Admin contact:</p>
                            <a
                                href={`sms:${supportPhone.replace(/[^0-9]/g, '')}`}
                                className="inline-flex items-center gap-2 text-[#FFD700] font-bold text-lg hover:underline"
                                data-testid="cashout-support-phone"
                            >
                                <MessageSquare className="w-4 h-4" />
                                {supportPhone}
                            </a>
                            <p className="text-[10px] text-[#A0AAB2] mt-1.5">
                                Text only · Hours: 8am – 10pm daily
                            </p>
                        </div>
                        <Button
                            onClick={() => setShowBrandedReceipt(true)}
                            className="mt-5 w-full font-black flex items-center justify-center gap-2"
                            style={{
                                background: 'linear-gradient(180deg, #FFE55C 0%, #FFD700 50%, #B8860B 100%)',
                                color: '#0a0a0a',
                                boxShadow: '0 4px 14px rgba(255,215,0,0.35)',
                            }}
                            data-testid="cashout-save-receipt"
                        >
                            <Download className="w-4 h-4" /> Save / share receipt
                        </Button>
                        <Button
                            onClick={() => handleDialogChange(false)}
                            className="btn-primary mt-2.5 w-full"
                            data-testid="cashout-success-close"
                        >
                            Done
                        </Button>

                        <BrandedReceipt
                            open={showBrandedReceipt}
                            onClose={() => setShowBrandedReceipt(false)}
                            receipt={{
                                kind: 'cashout',
                                gross: submittedAmount,
                                fee: submittedFee,
                                net: submittedNet,
                                method: submittedMethod,
                                handle: submittedHandle,
                                etaHours: submittedEtaHours,
                                afterHours: submittedAfterHours,
                                date: new Date().toISOString(),
                                reference: submittedRef,
                            }}
                        />
                    </div>
                ) : (
                    <>
                        <div className="space-y-4 py-4">
                            {/* ============================================
                                LOAD-WARNING BANNER — Feb 2026
                                Players sometimes see the cashier's Cash App /
                                Venmo / Chime handle on their incoming payout
                                and then try to send NEXT week's LOAD back to
                                that same handle. Those funds get lost — loads
                                MUST go through the official PayPal link. Big
                                red banner at the very top of the cashout
                                dialog so nobody misses it.
                               ============================================ */}
                            <div
                                className="rounded-xl p-4 border-2 flex items-start gap-3"
                                style={{
                                    background: 'linear-gradient(135deg, rgba(255,59,48,0.16), rgba(255,0,127,0.06))',
                                    borderColor: 'rgba(255,59,48,0.7)',
                                    boxShadow: '0 0 22px -6px rgba(255,59,48,0.35)',
                                }}
                                data-testid="cashout-load-warning"
                            >
                                <div className="shrink-0 mt-0.5">
                                    <AlertTriangle className="w-6 h-6 text-[#FF3B30]" />
                                </div>
                                <div className="flex-1 leading-snug">
                                    <p
                                        className="font-black uppercase tracking-wide text-[#FF3B30] mb-1"
                                        style={{ fontSize: 'clamp(0.95rem, 3.5vw, 1.1rem)' }}
                                    >
                                        Loading money? Use the PayPal link ONLY
                                    </p>
                                    <p className="text-white font-bold text-sm">
                                        Do <span className="text-[#FF3B30]">NOT</span> send loads to any
                                        Cash App / Venmo / Chime / Zelle handle you received a payout
                                        from — those funds will <span className="underline">NOT</span> be
                                        credited to your account.
                                    </p>
                                    <p className="text-[11px] text-[#FFB84D] mt-1.5 font-bold">
                                        Only the official PayPal load link on your dashboard adds credits.
                                    </p>
                                </div>
                            </div>

                            {/* PROMINENT after-hours nudge — fires the instant a
                                player opens the dialog during 10 PM – 8 AM CT
                                so nobody's surprised by the overnight wait.
                                Shown ABOVE the value banner so it can't be
                                missed. Only rendered when currently off-hours;
                                the daytime path still gets the small italic
                                footer inside the value banner. */}
                            {isAfterHoursCT() && (
                                <div
                                    className="rounded-xl p-3.5 flex gap-3 items-start border-2"
                                    style={{
                                        background: 'linear-gradient(135deg, rgba(255,184,77,0.14), rgba(255,140,0,0.05))',
                                        borderColor: 'rgba(255,184,77,0.55)',
                                    }}
                                    data-testid="cashout-after-hours-overlay"
                                >
                                    <div className="shrink-0 mt-0.5">
                                        <Clock className="w-5 h-5 text-[#FFB84D]" />
                                    </div>
                                    <div className="text-[12px] leading-snug">
                                        <p className="text-[#FFB84D] font-black text-sm mb-0.5">
                                            Payout desk is closed right now
                                        </p>
                                        <p className="text-white">
                                            Open <span className="font-bold">8 AM – 10 PM CT</span> daily.
                                            You can still request a cashout — it&apos;ll be first in line
                                            when customer service reopens at
                                            {' '}<span className="text-[#FFB84D] font-bold">8 AM CT</span>.
                                        </p>
                                        {openCountdown && (
                                            <p
                                                className="mt-2 text-[11px] text-[#A0AAB2] flex items-center gap-1.5"
                                                data-testid="cashout-after-hours-countdown"
                                            >
                                                <span className="inline-block w-1.5 h-1.5 rounded-full bg-[#FFB84D] animate-pulse" />
                                                Reopens in{' '}
                                                <span className="text-[#FFB84D] font-black tabular-nums">
                                                    {openCountdown}
                                                </span>
                                            </p>
                                        )}
                                    </div>
                                </div>
                            )}
                            {/* Upfront value banner — leads with the payout
                                convenience so the 6% fee lands as a fair trade.
                                (Jun 2026: dropped the "3-hour" time promise.) */}
                            <div
                                className="rounded-xl p-3 flex gap-3 items-start"
                                style={{
                                    background: 'linear-gradient(135deg, rgba(57,255,20,0.10), rgba(255,215,0,0.08))',
                                    border: '1px solid rgba(57,255,20,0.35)',
                                }}
                                data-testid="cashout-value-banner"
                            >
                                <div className="shrink-0 mt-0.5">
                                    <Zap className="w-5 h-5 text-[#39FF14]" />
                                </div>
                                <div className="text-[12px] leading-snug">
                                    <p className="text-white font-bold mb-0.5">
                                        Fast payouts — <span className="text-[#39FF14]">processed as quickly as possible</span>
                                    </p>
                                    <p className="text-[#A0AAB2]">
                                        Fast payouts run on a <span className="text-white font-semibold">6% processing fee</span> —
                                        but every penny of it comes back to you this coming
                                        {' '}<span className="text-[#FFD700] font-black">Monday as Free Play</span> (1× rollover).
                                        You essentially pay <span className="text-[#39FF14] font-bold">nothing</span> for speed.
                                    </p>
                                    {/* Payout-desk hours — non-alarming footer so players
                                        know late-night requests wait until morning. */}
                                    <p
                                        className="mt-1.5 text-[10.5px] text-[#A0AAB2] italic"
                                        data-testid="cashout-hours-notice"
                                    >
                                        Payout desk: <span className="text-white">8 AM – 10 PM CT</span> daily.
                                        Requests after 10 PM are processed the next morning at 8 AM.
                                    </p>
                                </div>
                            </div>

                            {/* ==============================================
                                QUICK CASHOUT CARD — Feb 2026
                                Shown when everything TapSNDR needs is already
                                on file. Two-tap flow: pick an amount chip,
                                hit Cash Out. "Change details" reveals the
                                full form for edits (new handle, new QR, etc).
                                ============================================== */}
                            {quickModeEligible && !showFullForm && (
                                <div
                                    className="rounded-xl p-4 space-y-3 border-2"
                                    style={{
                                        background: 'linear-gradient(135deg, rgba(255,215,0,0.08), rgba(57,255,20,0.05))',
                                        borderColor: 'rgba(255,215,0,0.45)',
                                    }}
                                    data-testid="quick-cashout-card"
                                >
                                    <div className="flex items-center justify-between gap-2 flex-wrap">
                                        <div className="flex items-center gap-2">
                                            <Zap className="w-4 h-4 text-[#FFD700]" />
                                            <p className="text-[#FFD700] font-black text-sm uppercase tracking-wider">
                                                Quick Cash Out
                                            </p>
                                        </div>
                                        <span className="text-[10px] text-[#A0AAB2] uppercase tracking-wider">
                                            2 taps · info on file
                                        </span>
                                    </div>

                                    {/* Compact info summary — everything the
                                        cashier needs to send this player,
                                        already saved. Clicking anywhere here
                                        opens the full form for edits. */}
                                    <button
                                        type="button"
                                        onClick={() => setShowFullForm(true)}
                                        className="w-full text-left bg-black/40 rounded-lg p-2.5 hover:bg-black/60 transition-colors border border-white/5"
                                        data-testid="quick-cashout-edit-details"
                                        title="Tap to change any of your saved details"
                                    >
                                        <div className="text-[11px] text-[#A0AAB2] flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                                            <span className="font-bold text-white">{payoutMethod}</span>
                                            {needsHandle && payoutHandle && (
                                                <span className="text-[#FFD700] font-mono">{payoutHandle}</span>
                                            )}
                                            <span className="opacity-60">·</span>
                                            <span>{payoutNameOnAccount}</span>
                                            <span className="opacity-60">·</span>
                                            <span className="truncate max-w-[180px]">{payoutEmail}</span>
                                        </div>
                                        <div className="text-[10px] text-[#39FF14] mt-1 font-bold">
                                            Change details →
                                        </div>
                                    </button>

                                    {/* Amount picker — chips + "All" + custom */}
                                    <div>
                                        <p className="text-[11px] text-[#A0AAB2] mb-1.5 uppercase tracking-wider font-bold">
                                            Amount · Balance <span className="text-white">${availableCredits.toFixed(2)}</span>
                                        </p>
                                        <div className="grid grid-cols-3 gap-1.5">
                                            {quickAmounts.map((v) => (
                                                <button
                                                    key={v}
                                                    type="button"
                                                    onClick={() => setCashOutAmount(String(v))}
                                                    className={`py-2 px-2 rounded-lg text-xs font-black border transition-colors ${
                                                        Number(cashOutAmount) === v
                                                            ? 'bg-[#FFD700] text-black border-[#FFD700]'
                                                            : 'bg-[#0a0a0a] text-white border-white/10 hover:border-[#FFD700]/50'
                                                    }`}
                                                    data-testid={`quick-cashout-amount-${v}`}
                                                >
                                                    ${v}
                                                </button>
                                            ))}
                                            <button
                                                type="button"
                                                onClick={() => setCashOutAmount(String(availableCredits))}
                                                className={`py-2 px-2 rounded-lg text-xs font-black border transition-colors ${
                                                    Number(cashOutAmount) === availableCredits
                                                        ? 'bg-[#39FF14] text-black border-[#39FF14]'
                                                        : 'bg-[#0a0a0a] text-[#39FF14] border-[#39FF14]/40 hover:bg-[#39FF14]/10'
                                                }`}
                                                data-testid="quick-cashout-amount-all"
                                            >
                                                All ${availableCredits.toFixed(0)}
                                            </button>
                                        </div>
                                        <Input
                                            type="number"
                                            min={MIN_CASHOUT}
                                            step="0.01"
                                            value={cashOutAmount}
                                            onChange={(e) => setCashOutAmount(e.target.value)}
                                            placeholder="Or type a custom amount"
                                            className="mt-2 bg-[#0a0a0a] border-white/10 text-white text-sm h-9"
                                            data-testid="quick-cashout-amount-input"
                                        />
                                    </div>

                                    {/* Live fee/net preview only when a valid
                                        amount is chosen, so the chip taps feel
                                        instant instead of noisy. */}
                                    {parsedAmount >= MIN_CASHOUT && parsedAmount <= availableCredits && (
                                        <div className="flex items-center justify-between text-[11px] bg-black/40 rounded-lg px-2.5 py-2">
                                            <span className="text-[#A0AAB2]">
                                                Fee 6% <span className="text-red-400">−${feeAmount.toFixed(2)}</span>
                                            </span>
                                            <span className="text-white">
                                                You get{' '}
                                                <span className="text-[#39FF14] font-black">${netAmount.toFixed(2)}</span>
                                            </span>
                                            <span className="text-[#FFD700] text-[10px]">
                                                Monday: ${feeAmount.toFixed(2)} back
                                            </span>
                                        </div>
                                    )}

                                    {/* Large-payout ack — inline in Quick so we
                                        still get consent without kicking them
                                        out to the full form. */}
                                    {isLargePayout && (
                                        <label className="flex items-start gap-2 cursor-pointer text-[11px] bg-black/40 rounded-lg px-2.5 py-2 border border-[#FFD700]/30">
                                            <input
                                                type="checkbox"
                                                checked={acknowledgedLarge}
                                                onChange={(e) => setAcknowledgedLarge(e.target.checked)}
                                                className="w-3.5 h-3.5 accent-[#FFD700] mt-0.5"
                                                data-testid="quick-acknowledge-large-payout"
                                            />
                                            <span className="text-white">
                                                Over $1,000 — allow up to {LARGE_PAYOUT_HOURS} hours
                                            </span>
                                        </label>
                                    )}

                                    <Button
                                        onClick={handleCashOut}
                                        disabled={
                                            cashOutLoading ||
                                            !cashOutAmount ||
                                            parsedAmount < MIN_CASHOUT ||
                                            parsedAmount > availableCredits ||
                                            (isLargePayout && !acknowledgedLarge)
                                        }
                                        className="btn-primary w-full font-black text-base py-5"
                                        data-testid="quick-cashout-submit"
                                    >
                                        {cashOutLoading
                                            ? 'Processing...'
                                            : parsedAmount >= MIN_CASHOUT
                                                ? `Cash Out $${parsedAmount.toFixed(2)}`
                                                : 'Pick an amount above'}
                                    </Button>

                                    {cashOutError && (
                                        <p className="text-red-400 text-xs" data-testid="quick-cashout-error">
                                            {cashOutError}
                                        </p>
                                    )}
                                </div>
                            )}

                            {/* Full form — always rendered when Quick isn't
                                available, or when the player tapped "Change
                                details" to edit. */}
                            {(!quickModeEligible || showFullForm) && (
                            <>
                            {quickModeEligible && showFullForm && (
                                <button
                                    type="button"
                                    onClick={() => setShowFullForm(false)}
                                    className="text-[11px] text-[#39FF14] font-bold hover:underline"
                                    data-testid="quick-cashout-back"
                                >
                                    ← Back to Quick Cash Out
                                </button>
                            )}
                            <div className="space-y-2">
                                <Label htmlFor="cashout-amount" className="text-white">Amount</Label>
                                <Input
                                    id="cashout-amount"
                                    type="number"
                                    min={MIN_CASHOUT}
                                    step="0.01"
                                    value={cashOutAmount}
                                    onChange={(e) => setCashOutAmount(e.target.value)}
                                    placeholder={`Enter amount (min $${MIN_CASHOUT})`}
                                    className="bg-[#0a0a0a] border-white/10 text-white"
                                    data-testid="cashout-amount"
                                />
                                <p className="text-[10px] text-[#A0AAB2]">Minimum cashout: ${MIN_CASHOUT}.00</p>
                            </div>

                            {/* Fee breakdown — 6% deducted at payout time,
                                rebated Monday as Free Play (1× wagering). */}
                            {parsedAmount >= MIN_CASHOUT && (
                                <div
                                    className="rounded-xl p-3 space-y-1.5 text-[12px]"
                                    style={{
                                        background: 'linear-gradient(135deg, rgba(255,215,0,0.06), rgba(0,0,0,0))',
                                        border: '1px solid rgba(255,215,0,0.3)',
                                    }}
                                    data-testid="cashout-fee-breakdown"
                                >
                                    <div className="flex justify-between text-[#A0AAB2]">
                                        <span>Cashout amount</span>
                                        <span className="text-white font-mono">${parsedAmount.toFixed(2)}</span>
                                    </div>
                                    <div className="flex justify-between text-[#A0AAB2]">
                                        <span>Processing fee (6%)</span>
                                        <span className="text-red-400 font-mono">−${feeAmount.toFixed(2)}</span>
                                    </div>
                                    <div className="border-t border-white/10 pt-1.5 flex justify-between">
                                        <span className="text-white font-bold">You&apos;ll receive</span>
                                        <span className="text-[#39FF14] font-mono font-black">${netAmount.toFixed(2)}</span>
                                    </div>
                                    <div className="mt-2 rounded-lg px-2.5 py-2 text-[10.5px] leading-snug"
                                         style={{ background: 'rgba(57,255,20,0.08)', border: '1px dashed rgba(57,255,20,0.4)' }}>
                                        🎁 <span className="text-[#39FF14] font-black">${feeAmount.toFixed(2)} back this coming Monday</span>
                                        <span className="text-[#A0AAB2]"> as </span>
                                        <span className="text-[#FFD700] font-black">Free Play</span>
                                        <span className="text-[#A0AAB2]"> — money to play with, NOT cash. Play through it once and any winnings become cashable.</span>
                                    </div>
                                    <div className="text-[10px] text-[#A0AAB2] pt-1">
                                        ⏱️ Payout speed: <span className="text-white font-bold">
                                            {isLargePayout ? `up to ${LARGE_PAYOUT_HOURS} hours` : 'as fast as possible (payout desk hours)'}
                                        </span>
                                    </div>
                                </div>
                            )}

                            <div className="space-y-2">
                                <Label className="text-white">How do you want to be paid?</Label>
                                {enabledMethods.length === 0 ? (
                                    <p className="text-xs text-[#A0AAB2] bg-[#0a0a0a] rounded-lg p-3 border border-white/10">
                                        No payout methods are currently available. Please contact your admin.
                                    </p>
                                ) : (
                                    <div className="grid grid-cols-3 gap-2">
                                        {enabledMethods.map(m => (
                                            <button
                                                type="button"
                                                key={m.id}
                                                onClick={() => { setPayoutMethod(m.id); if (!m.needs_handle) setPayoutHandle(''); }}
                                                className={`py-2.5 px-2 rounded-lg text-xs font-bold border transition-colors ${
                                                    payoutMethod === m.id
                                                        ? 'bg-[#FFD700] text-black border-[#FFD700]'
                                                        : 'bg-[#0a0a0a] text-white border-white/10 hover:border-[#FFD700]/40'
                                                }`}
                                                data-testid={`cashout-method-${m.id.replace(/\s+/g, '-')}`}>
                                                {m.label}
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>

                            {needsHandle && selectedMethod && (
                                <div className="space-y-2">
                                    <Label htmlFor="cashout-handle" className="text-white">
                                        Your {selectedMethod.label} handle
                                    </Label>
                                    <Input
                                        id="cashout-handle"
                                        value={payoutHandle}
                                        onChange={(e) => setPayoutHandle(e.target.value)}
                                        placeholder={selectedMethod.placeholder}
                                        className="bg-[#0a0a0a] border-white/10 text-white"
                                        data-testid="cashout-handle"
                                    />
                                    {/* Format hint + inline validation. The hint always shows so
                                        players know the expected shape (e.g. "$Cashtag" for Cash App,
                                        "10-digit phone or email" for Zelle). If they've typed
                                        something that doesn't match the method's regex, we surface
                                        a soft warning — not blocking, just guidance. */}
                                    {(selectedMethod.hint || selectedMethod.pattern) && (() => {
                                        const trimmed = (payoutHandle || '').trim();
                                        let mismatch = false;
                                        let regexOk = true;
                                        if (trimmed && selectedMethod.pattern) {
                                            try {
                                                regexOk = new RegExp(selectedMethod.pattern).test(trimmed);
                                                mismatch = !regexOk;
                                            } catch { /* bad regex — silently accept */ }
                                        }
                                        return (
                                            <div className="space-y-1" data-testid="cashout-handle-hint-block">
                                                {selectedMethod.hint && (
                                                    <p className="text-[11px] text-[#A0AAB2] flex items-start gap-1.5">
                                                        <span className="text-[#FFD700] flex-shrink-0">▸</span>
                                                        <span>{selectedMethod.hint}</span>
                                                    </p>
                                                )}
                                                {trimmed && trimmed.length < 3 && (
                                                    <p className="text-[11px] text-[#FFB84D]" data-testid="cashout-handle-warn-short">
                                                        Too short — please enter your full {selectedMethod.label} handle.
                                                    </p>
                                                )}
                                                {mismatch && trimmed.length >= 3 && (
                                                    <p className="text-[11px] text-[#FFB84D]" data-testid="cashout-handle-warn-format">
                                                        Doesn&apos;t look like a valid {selectedMethod.label} handle. Double-check the format above.
                                                    </p>
                                                )}
                                                {regexOk && trimmed.length >= 3 && (
                                                    <p className="text-[11px] text-[#39FF14]" data-testid="cashout-handle-ok">
                                                        ✓ Looks good
                                                    </p>
                                                )}
                                            </div>
                                        );
                                    })()}
                                    {/* Recent-handles chips — one-tap prefill from past cashouts
                                        for this specific method. Only shows when we have any. */}
                                    {(handleBook[selectedMethod.id] || []).length > 0 && (
                                        <div className="flex items-center gap-1.5 flex-wrap" data-testid="cashout-handle-book">
                                            <span className="text-[10px] text-[#A0AAB2] uppercase tracking-wider mr-1">
                                                Recent:
                                            </span>
                                            {(handleBook[selectedMethod.id] || []).slice(0, 5).map((h) => (
                                                <span
                                                    key={h}
                                                    className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-mono border transition ${
                                                        payoutHandle === h
                                                            ? 'border-[#FFD700] bg-[#FFD700]/15 text-[#FFD700]'
                                                            : 'border-white/15 bg-white/5 text-white/80 hover:border-[#FFD700]/50'
                                                    }`}
                                                    data-testid={`cashout-handle-chip-${h.replace(/[^a-zA-Z0-9]/g, '-')}`}
                                                >
                                                    <button
                                                        type="button"
                                                        onClick={() => setPayoutHandle(h)}
                                                        title={`Use ${h}`}
                                                    >
                                                        {h}
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className="opacity-60 hover:opacity-100"
                                                        title="Forget this handle"
                                                        onClick={async () => {
                                                            try {
                                                                await axios.post(`${API}/user/cashout-handle-book/hide`, {
                                                                    method: selectedMethod.id, handle: h,
                                                                }, { withCredentials: true });
                                                                setHandleBook(prev => ({
                                                                    ...prev,
                                                                    [selectedMethod.id]: (prev[selectedMethod.id] || []).filter(x => x !== h),
                                                                }));
                                                            } catch { /* silent */ }
                                                        }}
                                                    >
                                                        ×
                                                    </button>
                                                </span>
                                            ))}
                                        </div>
                                    )}
                                    <p className="text-[10px] text-[#A0AAB2]">
                                        Make sure this is correct — admin will send your payout here.
                                    </p>
                                    {/* Optional QR upload for Cash App / Venmo. Kept optional —
                                        the handle text is still the source of truth; the QR is
                                        just a scan-shortcut for the cashier. Client caps the
                                        image at ~800 KB. */}
                                    {['Cash App', 'Venmo', 'Chime'].includes(selectedMethod.id) && (
                                        <div className="pt-1">
                                            <label className="inline-flex items-center gap-2 cursor-pointer text-[11px] text-[#00FFFF] hover:text-[#39FF14] transition">
                                                <input
                                                    type="file"
                                                    accept="image/png,image/jpeg,image/webp"
                                                    className="hidden"
                                                    data-testid="cashout-qr-input"
                                                    onChange={(e) => {
                                                        const f = e.target.files?.[0];
                                                        if (!f) return;
                                                        if (f.size > 900_000) {
                                                            toast.error('QR image is too big — please use a screenshot under 900 KB');
                                                            e.target.value = '';
                                                            return;
                                                        }
                                                        const reader = new FileReader();
                                                        reader.onload = () => setPayoutQrDataUrl(String(reader.result || ''));
                                                        reader.readAsDataURL(f);
                                                    }}
                                                />
                                                <span className="text-lg leading-none">📷</span>
                                                {payoutQrDataUrl ? 'QR attached — swap' : 'Attach QR (optional, faster to send)'}
                                            </label>
                                            {payoutQrDataUrl && (
                                                <div className="mt-2 space-y-2">
                                                    <div className="flex items-center gap-2">
                                                        <img
                                                            src={payoutQrDataUrl}
                                                            alt="Your payment QR"
                                                            className="w-16 h-16 rounded-lg border border-white/10 object-cover"
                                                            data-testid="cashout-qr-preview"
                                                        />
                                                        {savedQrs[selectedMethod.id] && (
                                                            <span className="text-[10px] font-bold text-[#39FF14] bg-[#39FF14]/10 px-2 py-0.5 rounded border border-[#39FF14]/40">
                                                                ✓ Saved
                                                            </span>
                                                        )}
                                                        <button
                                                            type="button"
                                                            onClick={async () => {
                                                                setPayoutQrDataUrl('');
                                                                // Also clear saved QR on the profile so it
                                                                // doesn't auto-reload next time.
                                                                try {
                                                                    await axios.post(`${API}/user/save-payout-qr`, {
                                                                        method: selectedMethod.id, data_url: '',
                                                                    }, { withCredentials: true });
                                                                    setSavedQrs(prev => { const c = { ...prev }; delete c[selectedMethod.id]; return c; });
                                                                } catch { /* silent */ }
                                                            }}
                                                            className="text-[10px] text-[#A0AAB2] hover:text-white underline"
                                                            data-testid="cashout-qr-remove"
                                                        >
                                                            Remove
                                                        </button>
                                                    </div>
                                                    <label className="flex items-center gap-2 text-[10.5px] text-[#A0AAB2] cursor-pointer select-none">
                                                        <input
                                                            type="checkbox"
                                                            checked={saveQrToProfile}
                                                            onChange={(e) => setSaveQrToProfile(e.target.checked)}
                                                            className="accent-[#FFD700]"
                                                            data-testid="cashout-qr-save-checkbox"
                                                        />
                                                        Save this QR to my profile so I don&apos;t need to upload again
                                                    </label>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* TapSNDR redemption ticket fields (Aug 2026) — the payout
                                desk types these into redeem.tapsndr.com to send funds.
                                Prefilled from the player's last cashout so repeat
                                requests are one-tap. */}
                            <div className="space-y-2">
                                <Label htmlFor="cashout-email" className="text-white">
                                    Your email
                                </Label>
                                <Input
                                    id="cashout-email"
                                    type="email"
                                    value={payoutEmail}
                                    onChange={(e) => setPayoutEmail(e.target.value)}
                                    placeholder="name@example.com"
                                    className="bg-[#0a0a0a] border-white/10 text-white"
                                    data-testid="cashout-email"
                                />
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="cashout-name" className="text-white">
                                    First &amp; last name on your payment account
                                </Label>
                                <Input
                                    id="cashout-name"
                                    value={payoutNameOnAccount}
                                    onChange={(e) => setPayoutNameOnAccount(e.target.value)}
                                    placeholder="Jane Doe"
                                    className="bg-[#0a0a0a] border-white/10 text-white"
                                    data-testid="cashout-name-on-account"
                                    autoComplete="name"
                                />
                                <p className="text-[10px] text-[#A0AAB2]">
                                    Must match the name on the account we&apos;re sending to — helps avoid delays.
                                </p>
                            </div>

                            {payoutMethod === 'Cash' && (
                                <div className="bg-[#0a0a0a] rounded-lg p-3 border border-[#FFD700]/20">
                                    <p className="text-xs text-[#A0AAB2]">
                                        Cash payouts are picked up in person. Your admin will contact you at <span className="text-[#FFD700] font-bold">{supportPhone}</span> to arrange pickup. <span className="text-[#FFD700]/80 font-bold">Hours: 8am – 10pm daily.</span>
                                    </p>
                                </div>
                            )}

                            {/* Large Cash Out — 48-hour SLA notice */}
                            {isLargePayout && (
                                <div
                                    className="rounded-lg p-3.5 border-2"
                                    style={{
                                        background: 'linear-gradient(135deg, rgba(255,215,0,0.10), rgba(255,140,0,0.06))',
                                        borderColor: 'rgba(255,215,0,0.45)',
                                    }}
                                    data-testid="large-payout-notice"
                                >
                                    <div className="flex items-start gap-2 mb-2">
                                        <AlertTriangle className="w-4 h-4 text-[#FFD700] mt-0.5 flex-shrink-0" />
                                        <div>
                                            <p className="text-[#FFD700] font-bold text-sm">
                                                Heads up — larger cash out
                                            </p>
                                            <p className="text-white text-xs mt-1 leading-relaxed">
                                                For amounts over <span className="font-bold">${LARGE_PAYOUT_THRESHOLD.toLocaleString()}</span>,
                                                please allow up to <span className="text-[#FFD700] font-bold">{LARGE_PAYOUT_HOURS} hours</span> to
                                                receive payment. Most go out same day, but our admin team may need extra time on bigger payouts.
                                            </p>
                                        </div>
                                    </div>
                                    <label className="flex items-center gap-2 cursor-pointer mt-2 select-none">
                                        <input
                                            type="checkbox"
                                            checked={acknowledgedLarge}
                                            onChange={(e) => setAcknowledgedLarge(e.target.checked)}
                                            className="w-4 h-4 accent-[#FFD700]"
                                            data-testid="acknowledge-large-payout"
                                        />
                                        <span className="text-xs text-white font-medium">
                                            I understand and agree to allow up to {LARGE_PAYOUT_HOURS} hours for payment.
                                        </span>
                                    </label>
                                </div>
                            )}

                            {cashOutError && (
                                <p className="text-red-400 text-sm" data-testid="cashout-error">{cashOutError}</p>
                            )}
                            </>
                            )}
                        </div>
                        {(!quickModeEligible || showFullForm) && (
                        <DialogFooter>
                            <Button
                                onClick={handleCashOut}
                                disabled={cashOutLoading || !cashOutAmount || !payoutMethod || (isLargePayout && !acknowledgedLarge)}
                                className="btn-primary w-full"
                                data-testid="cashout-submit"
                            >
                                {cashOutLoading
                                    ? 'Processing...'
                                    : isLargePayout
                                        ? `Submit (allow up to ${LARGE_PAYOUT_HOURS}hr)`
                                        : 'Submit Request'}
                            </Button>
                        </DialogFooter>
                        )}
                    </>
                )}
            </DialogContent>
        </Dialog>
    );
};

export default CashOutDialog;
